import _extends from "@babel/runtime/helpers/extends";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _excluded = ["columns", "data", "aria-labelledby", "getRowAttributes", "rowKeyAccessor", "spacing", "isStriped", "hasStickyHeader", "shouldScrollHorizontally", "ascendingTitleText", "descendingTitleText", "shouldAutoCollapseOnExpand", "maxWidth", "maxHeight", "actionHeader", "actionColumnIndex", "onSort", "sortDirection", "activeSortId"];

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { pickDataProps } from "../../utils";
import { defaultProps, defaultRowAttributes } from "../constants";
import { mapIndexesToBooleans, setAllValuesToFalse } from "../expandable";
import { TableElement as Table, TbodyElement as Tbody } from "../html";
import { applyKeyAccessor } from "../utils";
import ScrollWrapper from "./ScrollWrapper";
import TableHeader from "./TableHeader";
import TableRow from "./TableRow";
import { jsx as ___EmotionJSX } from "@emotion/core";

var TableLayout = function TableLayout(_ref) {
  var columns = _ref.columns,
      data = _ref.data,
      ariaLabelledby = _ref['aria-labelledby'],
      _ref$getRowAttributes = _ref.getRowAttributes,
      getRowAttributes = _ref$getRowAttributes === void 0 ? defaultProps.getRowAttributes : _ref$getRowAttributes,
      rowKeyAccessor = _ref.rowKeyAccessor,
      spacing = _ref.spacing,
      isStriped = _ref.isStriped,
      hasStickyHeader = _ref.hasStickyHeader,
      _ref$shouldScrollHori = _ref.shouldScrollHorizontally,
      shouldScrollHorizontally = _ref$shouldScrollHori === void 0 ? defaultProps.shouldScrollHorizontally : _ref$shouldScrollHori,
      ascendingTitleText = _ref.ascendingTitleText,
      descendingTitleText = _ref.descendingTitleText,
      shouldAutoCollapseOnExpand = _ref.shouldAutoCollapseOnExpand,
      maxWidth = _ref.maxWidth,
      maxHeight = _ref.maxHeight,
      _ref$actionHeader = _ref.actionHeader,
      actionHeader = _ref$actionHeader === void 0 ? defaultProps.actionHeader : _ref$actionHeader,
      actionColumnIndex = _ref.actionColumnIndex,
      onSort = _ref.onSort,
      sortDirection = _ref.sortDirection,
      activeSortId = _ref.activeSortId,
      forwardedProps = _objectWithoutProperties(_ref, _excluded);

  var rowAttributes = useMemo(function () {
    return data.map(function (dataItem, index) {
      return _objectSpread(_objectSpread({}, defaultRowAttributes), getRowAttributes({
        data: dataItem,
        index: index
      }));
    });
  }, [data, getRowAttributes]);
  var isExpandable = rowAttributes.some(function (attributes) {
    return !!attributes.expandableContent;
  }) || columns.some(function (_ref2) {
    var expandableContent = _ref2.expandableContent;
    return expandableContent !== undefined;
  });
  var hasActionColumn = rowAttributes.some(function (attributes) {
    return !!attributes.onClick;
  }) || isExpandable;

  var _useState = useState(isExpandable ? mapIndexesToBooleans(data) : {}),
      _useState2 = _slicedToArray(_useState, 2),
      expandedRows = _useState2[0],
      setExpandedRows = _useState2[1]; // Used to indicate the table is to be sorted.


  var _useState3 = useState(false),
      _useState4 = _slicedToArray(_useState3, 2),
      isSorted = _useState4[0],
      setIsSorted = _useState4[1];

  var toggleExpandedHandler = useCallback(function (index) {
    setExpandedRows(function (prevExpandedRows) {
      return _objectSpread(_objectSpread({}, shouldAutoCollapseOnExpand ? setAllValuesToFalse(prevExpandedRows) : prevExpandedRows), {}, _defineProperty({}, index, !prevExpandedRows[index]));
    });
  }, [setExpandedRows, shouldAutoCollapseOnExpand]);
  var toggleExpanded = isExpandable ? toggleExpandedHandler : undefined;
  useEffect(function () {
    // Clean up after collapsing all rows so next row expansion has the animation enabled.
    if (isSorted) {
      setIsSorted(false);
    }
  }, [expandedRows, isSorted]); // onSort callback modified to collapse all expanded rows, isSorted set to true disables the animation in the ExpanderContent.

  var collapseAndOnSort = function collapseAndOnSort(_ref3) {
    var sortId = _ref3.sortId,
        sortDirection = _ref3.sortDirection;

    if (onSort) {
      setIsSorted(true);
      setExpandedRows(_objectSpread({}, setAllValuesToFalse(expandedRows)));
      onSort({
        sortId: sortId,
        sortDirection: sortDirection
      });
    }
  };

  var table = ___EmotionJSX(Table // If horizontal scrolling is enabled, allow the table to be as wide as possible
  // and add the max width to the scroll wrapper instead.
  , _extends({
    maxWidth: shouldScrollHorizontally ? undefined : maxWidth,
    spacing: spacing,
    isStriped: isStriped,
    hasStickyHeader: hasStickyHeader,
    "aria-labelledby": ariaLabelledby
  }, pickDataProps(forwardedProps)), ___EmotionJSX(TableHeader, {
    columns: columns,
    hasActionColumn: hasActionColumn,
    actionColumnIndex: actionColumnIndex,
    spacing: spacing,
    actionHeader: actionHeader,
    isStriped: isStriped,
    hasStickyHeader: hasStickyHeader,
    onSort: isExpandable ? collapseAndOnSort : onSort,
    sortDirection: sortDirection,
    activeSortId: activeSortId,
    ascendingTitleText: ascendingTitleText,
    descendingTitleText: descendingTitleText
  }), ___EmotionJSX(Tbody, null, data.map(function (dataItem, index) {
    var tableRowKey = applyKeyAccessor(rowKeyAccessor, dataItem, index);
    return ___EmotionJSX(TableRow, {
      key: tableRowKey,
      tableRowKey: tableRowKey,
      isStriped: isStriped,
      data: dataItem,
      index: index,
      columns: columns,
      isExpanded: isExpandable ? expandedRows[index] : undefined,
      toggleExpanded: toggleExpanded,
      actionColumnIndex: actionColumnIndex,
      attributes: rowAttributes[index],
      isSorted: isSorted,
      spacing: spacing
    });
  })));

  return ___EmotionJSX(ScrollWrapper, {
    shouldScrollHorizontally: shouldScrollHorizontally,
    maxWidth: maxWidth,
    maxHeight: maxHeight,
    hasStickyHeader: hasStickyHeader
  }, table);
};

TableLayout.displayName = 'TableLayout';
export default TableLayout;